import { useCallback, useMemo } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import {
  ButtonBase,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { format } from 'date-fns';
import { DefaultCard, moneyFormat } from 'design-system';

import { EMITTED } from 'data-layer-cr/dist/constants/quotationStatus';
import { QuotationSummaryResponse } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Analytics
import {
  ITEM_CLICKED,
  LIST_CLICK,
  THREE_DOTS_MENU,
  trackEvent,
} from 'src/analytics';

// Constants
import { APIBBC, APICCT } from 'src/constants/quotationOrigin';

// Redux
import {
  cloneAndEditQuotation,
  startCopyQuotation,
} from 'src/store/quotation/actions';
import { REQUEST_QUOTATION_CLONE } from 'src/store/quotation/requestIdentifiers';
import { RootState } from 'src/store/rootReducer';

// Functions
import renderRoleIcon from 'src/functions/renderRoleIcon';
import validateRole from 'src/functions/validateRole';

// Functions
import shouldShowEditButton from '../shouldShowEditButton';

// Components
import { CarIcon } from '../../../../../Icons';
import StatusDisplay from '../../../../../StatusDisplay';
import QuotationEditButton from '../QuotationEditButton';
import QuotationMenuButton from '../QuotationMenuButton';

// styles
import useQuotationListCardStyles from './styles';

const { formatMoney } = moneyFormat;
const COLON_MONEY = '₡';

/** Extracted component renders */
function renderQuotationStatus(statusDisplay: JSX.Element): JSX.Element {
  return (
    <ButtonBase disableRipple style={{ width: '100%', marginTop: '5px' }}>
      <Grid container>
        <Grid item xs={10} container alignItems="center">
          <CarIcon />
          <div style={{ marginLeft: 12 }}>{statusDisplay}</div>
        </Grid>
        <Grid
          item
          xs={2}
          container
          alignItems="center"
          justifyContent="flex-end"
        >
          <img alt="details" src="./svg/next_caret.svg" />
        </Grid>
      </Grid>
    </ButtonBase>
  );
}

function renderAnualPaymentSection(
  anualPayment: number,
  currency: string,
): JSX.Element {
  return (
    <>
      <Grid item xs={6}>
        <Typography
          variant="caption"
          style={{ fontSize: '17px', fontWeight: 700 }}
        >
          Prima Anual
        </Typography>
      </Grid>
      <Grid item xs={6} container justifyContent="flex-end">
        <Typography
          id="sales-web-label-paymentsum"
          style={{ fontSize: '23px', fontWeight: 900 }}
          variant="caption"
          align="right"
        >
          {formatMoney(anualPayment, true, currency)}
        </Typography>
      </Grid>
    </>
  );
}

function renderQuotationDate(dateString: string): JSX.Element {
  return (
    <>
      <Grid item xs={6}>
        <Typography
          style={{ fontSize: '13px', fontWeight: 500 }}
          variant="caption"
        >
          Cotizado {dateString}
        </Typography>
      </Grid>
      <Grid item xs={6} container justifyContent="flex-end">
        <Typography
          style={{ fontSize: '13px', fontWeight: 500 }}
          variant="caption"
          align="right"
        >
          Incluye el IVA
        </Typography>
      </Grid>
    </>
  );
}

function renderTotalAmountSection(
  insuranceValue: number,
  currency: string,
): JSX.Element {
  return (
    <>
      <Grid item xs={6}>
        <Typography
          variant="caption"
          style={{ fontSize: '17px', fontWeight: 700 }}
        >
          Suma Asegurada
        </Typography>
      </Grid>
      <Grid item xs={6} container justifyContent="flex-end">
        <Typography
          id="sales-web-label-insurancevalue"
          data-testid="total-amount"
          style={{ fontSize: '23px', fontWeight: 900 }}
          variant="caption"
          align="right"
        >
          {formatMoney(insuranceValue, false, currency)}
        </Typography>
      </Grid>
    </>
  );
}

export const QuotationListCard = ({
  quotationSummary,
  onClick,
  cloneAndEditQuotationAction,
  cloningQuotationId,
  onModificateVin,
  startCopyQuotationAction,
  id,
}: Props): JSX.Element => {
  const classes = useQuotationListCardStyles();

  const cloningStatus = useNetworkObserver(REQUEST_QUOTATION_CLONE);
  const modelBrand = useMemo(
    () => `${quotationSummary.carBrand} ${quotationSummary.carModel}`,
    [quotationSummary],
  );

  const isCloning = useMemo(
    () =>
      cloningStatus === 'requesting' &&
      cloningQuotationId === quotationSummary.id,
    [cloningStatus, quotationSummary, cloningQuotationId],
  );

  const dateString = useMemo(
    () => format(new Date(quotationSummary.quotationDate), 'dd/MM/yyyy'),
    [quotationSummary],
  );

  const statusDisplay = useMemo(
    () => <StatusDisplay quotationSummary={quotationSummary} />,
    [quotationSummary],
  );

  const onEditButtonClick = useCallback(() => {
    cloneAndEditQuotationAction(quotationSummary.id);
  }, [quotationSummary, cloneAndEditQuotationAction]);

  const handleClickCard = () => {
    trackEvent({
      eventName: THREE_DOTS_MENU,
      eventProperties: {
        [ITEM_CLICKED]: LIST_CLICK,
      },
    });
    onClick();
  };

  return (
    <div className={classes.rootContainer}>
      <DefaultCard
        onClick={handleClickCard}
        id={id}
        background={
          quotationSummary.origin === APIBBC ||
          quotationSummary.origin === APICCT
            ? 'linear-gradient(0.16deg, #FFFFFF 13.23%, #CEE8FF 105.34%)'
            : ''
        }
      >
        <div className={classes.root}>
          <Grid container>
            {validateRole(quotationSummary) && (
              <Grid item xs={12} container data-testid="role-section">
                <Grid item xs={10} container alignItems="center">
                  {renderRoleIcon(
                    quotationSummary.userRole,
                    quotationSummary.origin,
                    quotationSummary.fromPersonalLink,
                  )}
                  <Typography
                    id="sales-web-label-quotationcreator"
                    align="left"
                    variant="h4"
                    className={classes.rightAnchor}
                    data-testid="role-info"
                  >
                    {`${quotationSummary.quotationSequence} - ${quotationSummary.username}`}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  container
                  alignItems="center"
                  justifyContent="flex-end"
                  data-testid="quotation-menu-button-container"
                >
                  <QuotationMenuButton
                    data-testid="quotation-menu-button"
                    quotationId={quotationSummary.id}
                    onModificateVin={onModificateVin}
                    onShowDocuments={onClick}
                    onCopyQuotation={() =>
                      startCopyQuotationAction(
                        quotationSummary.urlSalt,
                        quotationSummary.id,
                      )
                    }
                    showVinOption={
                      quotationSummary.profile.type === 'colectiva'
                    }
                    isEmitted={quotationSummary.status.id === EMITTED}
                  />
                </Grid>
                <Grid item xs={12}>
                  <hr className={classes.borderHr} />
                </Grid>
              </Grid>
            )}
            <Grid container className={classes.container}>
              {shouldShowEditButton(quotationSummary) && (
                <div className={classes.editBtn}>
                  {!isCloning && (
                    <QuotationEditButton
                      data-testid="edit-button"
                      onConfirm={onEditButtonClick}
                      onReject={(): void => {
                        /** empty function */
                      }}
                    />
                  )}
                  {isCloning && (
                    <CircularProgress
                      data-testid="cloning-network-progress"
                      size={12}
                    />
                  )}
                </div>
              )}
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ fontWeight: 900, fontSize: '19px' }}
                  id="sales-web-label-customername"
                >
                  {quotationSummary.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  data-testid="car-description"
                  id="sales-web-label-carinfo"
                  variant="h3"
                  className={classes.carModelText}
                >
                  {modelBrand} / {quotationSummary.carYear}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {renderQuotationStatus(statusDisplay)}
              </Grid>

              <Grid item xs={12} className={classes.spacerMd} />

              <Grid item xs={12}>
                <Typography
                  id="sales-web-label-producttype"
                  variant="caption"
                  style={{ fontWeight: 400, fontSize: '15px' }}
                >
                  {quotationSummary.description}
                </Typography>
              </Grid>

              <Grid item xs={12} className={classes.spacer} />

              <Grid item xs={12} container>
                {renderAnualPaymentSection(
                  quotationSummary.anualPayment,
                  quotationSummary.profile.typeOfMoney || COLON_MONEY,
                )}
              </Grid>

              <Grid item xs={12} container>
                {renderQuotationDate(dateString)}
              </Grid>

              <Grid item xs={12}>
                <hr className={classes.borderEnsuranceSum} />
              </Grid>

              <Grid item xs={12} container alignItems="baseline">
                {renderTotalAmountSection(
                  quotationSummary.insuranceValue,
                  quotationSummary.profile.typeOfMoney || COLON_MONEY,
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
      </DefaultCard>
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  cloningQuotationId: number;
} => ({
  cloningQuotationId: state.quotation.cloningQuotationId,
});

const connector = connect(mapStateToProps, {
  cloneAndEditQuotationAction: cloneAndEditQuotation,
  startCopyQuotationAction: startCopyQuotation,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  quotationSummary: QuotationSummaryResponse;
  onClick: () => void;
  onModificateVin: () => void;
  id?: string;
}

export default connector(QuotationListCard);
